import { EmotionIcon } from '@emotion-icons/emotion-icon';
import React, { useContext, useState, useEffect } from 'react';
import { Property } from 'csstype';
import { DesignSystemContext } from '../../composites/DesignSystemContextProvider/DesignSystemContextProvider';

type IconLibrary = Record<string, EmotionIcon>;

interface IconLibraries {
  'ionicons-outline'?: IconLibrary;
  'ionicons-sharp'?: IconLibrary;
  'ionicons-solid'?: IconLibrary;
  [key: string]: IconLibrary | undefined;
}

// Icon cache to avoid re-importing
const iconCache: Record<string, EmotionIcon> = {};

// Preload common icon libraries to avoid dynamic import issues
const iconLibraries: IconLibraries = {
  // These will be lazily populated as icons are used
};

export interface IconProps {
  library?: 'ioniconOutline';
  name: string;
  size?: 'small' | 'medium' | 'large' | number;
  color?: Property.Color;
}

export const Icon = ({
  library = 'ioniconOutline',
  name,
  size = 'medium',
  color,
}: IconProps) => {
  const { theme } = useContext(DesignSystemContext);
  const [icon, setIcon] = useState<EmotionIcon | null>(null);
  const [loading, setLoading] = useState(false);

  const sizeMap = {
    small: theme.spacing(3),
    medium: theme.spacing(4),
    large: theme.spacing(5),
  };

  const cacheKey = `${library}.${name}`;

  useEffect(() => {
    if (iconCache[cacheKey]) {
      setIcon(iconCache[cacheKey]);
      return;
    }

    setLoading(true);

    const importIcon = async () => {
      try {
        let iconModule;

        // Map library names to actual package paths
        switch (library) {
          case 'ioniconOutline':
            if (!iconLibraries['ionicons-outline']) {
              const importedModule = await import(
                '@emotion-icons/ionicons-outline'
              );
              // Remove the default export that's causing the type error
              const { default: _, ...icons } = importedModule;
              iconLibraries['ionicons-outline'] = icons as IconLibrary;
            }
            iconModule = iconLibraries['ionicons-outline'];
            break;
          default:
            throw new Error(`Unsupported icon library: ${library}`);
        }

        if (iconModule && iconModule[name]) {
          iconCache[cacheKey] = iconModule[name];
          setIcon(iconModule[name]);
        } else {
          console.warn(`Icon "${name}" not found in "${library}"`);
          setIcon(null);
        }
      } catch (error) {
        console.error(`Failed to load icon: ${name} from ${library}`, error);
        setIcon(null);
      } finally {
        setLoading(false);
      }
    };

    importIcon();
  }, [library, name, cacheKey]);

  if (loading) {
    return <span>Loading...</span>;
  }

  if (!icon) {
    return null;
  }

  const IconComponent = icon;
  return (
    <IconComponent
      color={color || theme.palette.text.primary}
      size={typeof size === 'number' ? theme.spacing(size) : sizeMap[size]}
    />
  );
};
